import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  TextField,
  InputAdornment,
  Divider,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  Alert,
  Collapse,
  Chip,
} from "@mui/material";

import {
  Search,
  Security,
  CheckCircle,
  Person,
  AdminPanelSettings,
} from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";


import { getUsers } from "../../services/user";

import {
  fetchPermissionCatalog,
  fetchUserPermissions,
  updateUserPermissions,
  fetchRoles,
} from "../../services/rbac";


  const UserPanel = ({
    users,
    filteredUsers,
    activeUserId,
    loadingUsers,
    searchTerm,
    setSearchTerm,
    selectUser,
  }) => {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>

          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Person color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Admin Users
            </Typography>
          </Stack>

          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Divider sx={{ my: 2 }} />

          {loadingUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 520, overflowY: "auto" }}>
              {filteredUsers.map((user) => (
                <ListItemButton
                  key={user.sid}
                  selected={activeUserId === user.sid}
                  onClick={() => selectUser(user.sid)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemText
                    primary={user.fullname || user.username}
                    secondary={user.email || user.username}
                  />
                </ListItemButton>
              ))}

              {!filteredUsers.length && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  No users found
                </Typography>
              )}
            </List>
          )}

        </CardContent>
      </Card>
    );
  };

  const RolePanel = ({
  roles,
  roleSearchTerm,
  setRoleSearchTerm,
  selectedRoleIds,
  handleToggleRole,
  loadingUserPermissions,
}) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>

        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <AdminPanelSettings color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Roles
          </Typography>
        </Stack>

        <TextField
          value={roleSearchTerm}
          onChange={(e) => setRoleSearchTerm(e.target.value)}
          placeholder="Search roles"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Typography variant="body2" color="text.secondary" mb={2}>
          Assign predefined permission templates.
        </Typography>

        {loadingUserPermissions ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={1}>
            {roles.map((role) => (
              <FormControlLabel
                key={role.id}
                control={
                  <Checkbox
                    checked={selectedRoleIds.includes(role.id)}
                    onChange={() => handleToggleRole(role.id)}
                  />
                }
                label={
                  <Stack>
                    <Typography fontSize={14} fontWeight={500}>
                      {role.name}
                    </Typography>

                    {role.description && (
                      <Typography
                        fontSize={12}
                        color="text.secondary"
                      >
                        {role.description}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            ))}

            {!roles.length && (
              <Typography variant="body2" color="text.secondary">
                No roles created yet
              </Typography>
            )}
          </Stack>
        )}

      </CardContent>
    </Card>
  );
  };

  const PermissionCatalogPanel = ({
  catalog,
  permissionSearchTerm,
  setPermissionSearchTerm,
  selectedPermissionIds,
  assignedPermissionIds,
  handleTogglePermission,
  loadingCatalog,
}) => {

  if (loadingCatalog) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>

        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <Security color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Permission Catalog
          </Typography>
        </Stack>

        <TextField
          value={permissionSearchTerm}
          onChange={(e) => setPermissionSearchTerm(e.target.value)}
          placeholder="Search permissions"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack spacing={2} sx={{ maxHeight: 520, overflowY: "auto" }}>

          {catalog.map(({ category, permissions }) => (

            <Box key={category?.id || category?.code}>

              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mb: 1 }}
              >
                {category?.name || "Uncategorized"}
              </Typography>

              <Grid container spacing={1}>

                {permissions.map((permission) => {

                  const isDirect =
                    selectedPermissionIds.includes(permission.id);

                  const isInherited =
                    assignedPermissionIds.has(permission.id) && !isDirect;

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={permission.id}>

                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: isDirect
                            ? "primary.main"
                            : "divider",
                          borderRadius: 1,
                          p: 1.5,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >

                        <Checkbox
                          checked={isDirect}
                          onChange={() =>
                            handleTogglePermission(permission.id)
                          }
                        />

                        <Box>

                          <Typography
                            variant="body2"
                            fontWeight={600}
                          >
                            {permission.name}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {permission.identifier}
                          </Typography>

                          {isInherited && (
                            <Chip
                              icon={<CheckCircle fontSize="small" />}
                              label="Inherited"
                              size="small"
                              color="success"
                              sx={{ ml: 1 }}
                            />
                          )}

                        </Box>

                      </Box>

                    </Grid>
                  );
                })}
              </Grid>

            </Box>

          ))}

          {!catalog.length && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
            >
              No permissions available
            </Typography>
          )}

        </Stack>

      </CardContent>
    </Card>
  );
  };

  const EffectivePermissionsPanel = ({ effectivePermissions }) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>

        {/* Header */}
        <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Grid>
            <CheckCircle color="success" />
          </Grid>

          <Grid>
            <Typography variant="subtitle1" fontWeight={600}>
              Effective Permissions
            </Typography>
          </Grid>
        </Grid>

        {/* Description */}
        <Grid container sx={{ mb: 2 }}>
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary">
              Final permissions granted to this user after combining roles
              and direct assignments.
            </Typography>
          </Grid>
        </Grid>

        {/* Permissions List */}
        <Grid
          container
          spacing={1}
          sx={{
            maxHeight: 520,
            overflowY: "auto",
            pb: 2
          }}
        >
          {effectivePermissions.length > 0 ? (
            effectivePermissions.map((perm) => (
              <Grid key={perm.id}  size={{ xs: 12, sm: 6, md: 3 }}>
                <Chip
                  label={perm.name}
                  size="small"
                  color="success"
                  sx={{ width: "100%" }}
                />
              </Grid>
            ))
          ) : (
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">
                No permissions assigned
              </Typography>
            </Grid>
          )}
        </Grid>

      </CardContent>
    </Card>
  );
};

const AccessControlPage = () => {
   // USERS
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("");

  // DATA
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);

  // USER PERMISSIONS
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [effectivePermissions, setEffectivePermissions] = useState([]);

  // LOADING
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingUserPermissions, setLoadingUserPermissions] = useState(false);

  // UI
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // SEARCH



 useEffect(() => {
  const loadInitialData = async () => {
    try {

      const [
        userResponse,
        catalogResponse,
        rolesResponse
      ] = await Promise.all([
        getUsers({ page_size: 100, is_staff: true }),
        fetchPermissionCatalog(),
        fetchRoles({ page_size: 100 })
      ]);

      const userList = userResponse?.results || [];

      setUsers(userList);
      setFilteredUsers(userList);

      setCatalog(catalogResponse?.categories || []);
      setRoles(rolesResponse?.results || rolesResponse || []);

      if (userList.length > 0) {
        selectUser(userList[0].sid);
      }

    } catch (error) {

      setFeedback({
        type: "error",
        message: error.message || "Failed loading RBAC data"
      });

    } finally {

      setLoadingUsers(false);
      setLoadingCatalog(false);

    }
  };

  loadInitialData();
}, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredUsers(
      users.filter((user) =>
        [user.username, user.email, user.fullname]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(term))
      )
    );
  }, [searchTerm, users]);

  const filteredRoles = useMemo(() => {

  if (!roleSearchTerm) return roles;

  const term = roleSearchTerm.toLowerCase();

  return roles.filter((role) =>
    [role.name, role.description]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(term))
  );

  }, [roles, roleSearchTerm]);

  const filteredCatalog = useMemo(() => {

  if (!permissionSearchTerm) return catalog;

  const term = permissionSearchTerm.toLowerCase();

  return catalog
    .map((category) => {

      const filteredPermissions = category.permissions.filter((perm) =>
        [perm.name, perm.identifier]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(term))
      );

      if (!filteredPermissions.length) return null;

      return {
        ...category,
        permissions: filteredPermissions,
      };

    })
    .filter(Boolean);

}, [catalog, permissionSearchTerm]);


  const selectUser = async (userId) => {

      setActiveUserId(userId);
      setLoadingUserPermissions(true);
      setFeedback(null);

      try {

        const response = await fetchUserPermissions(userId);

        setSelectedRoleIds(response.role_ids || []);
        setSelectedPermissionIds(response.direct_permission_ids || []);
        setEffectivePermissions(response.effective_permissions || []);

      } catch (error) {

        setFeedback({
          type: "error",
          message: error.message || "Failed loading permissions"
        });

      } finally {

        setLoadingUserPermissions(false);

      }
  };

  const handleToggleRole = (roleId) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleTogglePermission = (permissionId) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const assignedPermissionIds = useMemo(() => {
    return new Set([
      ...(selectedPermissionIds || []),
      ...(effectivePermissions || []).map((perm) => perm.id),
    ]);

}, [selectedPermissionIds, effectivePermissions]);


  const handleSave = async () => {

      if (!activeUserId) return;

      setSaving(true);
      setFeedback(null);

      try {

        const response = await updateUserPermissions(activeUserId, {
          role_ids: selectedRoleIds,
          permission_ids: selectedPermissionIds,
        });

        setEffectivePermissions(response.effective_permissions || []);

        setFeedback({
          type: "success",
          message: "Permissions updated successfully",
        });

      } catch (error) {

        setFeedback({
          type: "error",
          message: error.message || "Failed to update permissions",
        });

      } finally {

      setSaving(false);

  }

  };


  return (
    <Grid container spacing={2}>

  {/* USERS */}
  <Grid size={{ xs: 12, md: 2 }}>
    <UserPanel
      users={users}
      filteredUsers={filteredUsers}
      activeUserId={activeUserId}
      loadingUsers={loadingUsers}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      selectUser={selectUser}
    />
  </Grid>

  {/* ROLES */}
  <Grid size={{ xs: 12, md: 2 }}>
    <RolePanel
      roles={filteredRoles}
      roleSearchTerm={roleSearchTerm}
      setRoleSearchTerm={setRoleSearchTerm}
      selectedRoleIds={selectedRoleIds}
      handleToggleRole={handleToggleRole}
      loadingUserPermissions={loadingUserPermissions}
    />
  </Grid>

  {/* PERMISSION CATALOG */}
  <Grid size={{ xs: 12, md: 8 }}>
    <PermissionCatalogPanel
      catalog={filteredCatalog}
      permissionSearchTerm={permissionSearchTerm}
      setPermissionSearchTerm={setPermissionSearchTerm}
      selectedPermissionIds={selectedPermissionIds}
      assignedPermissionIds={assignedPermissionIds}
      handleTogglePermission={handleTogglePermission}
      loadingCatalog={loadingCatalog}
    />
  </Grid>
   
   {/* ACTION BAR */}
  <Grid size={{ xs: 12 }}>

    <Card>
      <CardContent>

        <Grid container alignItems="center" spacing={2}>

          {/* Save Button */}
          <Grid>
            <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !activeUserId}
              >
                {saving ? "Saving..." : "Save Permissions"}
            </Button>
          </Grid>

          {/* Loading Spinner */}
          {saving && (
            <Grid>
              <CircularProgress size={20} />
            </Grid>
          )}

          {/* Feedback Message */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Collapse in={!!feedback}>
              {feedback && (
                <Alert severity={feedback.type}>
                  {feedback.message}
                </Alert>
              )}
            </Collapse>
          </Grid>

        </Grid>

      </CardContent>
    </Card>

  </Grid>

  {/* EFFECTIVE PERMISSIONS */}
  <Grid size={{ xs: 12, md: 12 }}>
    <EffectivePermissionsPanel
      effectivePermissions={effectivePermissions}
    />
  </Grid>

    </Grid>

  );

};

export default AccessControlPage;
